import type { IDistanceMeasureLinePropertyBagModel } from '$lib/models/IDistanceMeasureLinePropertyBagModel';
import type {
	ILinePropertyBagModel,
	SpanModel
} from '$lib/models/ILineDataModel';
import type { IMastPointFullModel } from '$lib/models/IMastPointFullModel';
import type { IMastPointMinimalModel } from '$lib/models/IMastPointMinimalModel';
import type {
	IDrawnPolygonPart,
	IDrawnPolygonShape,
	IPolygonLabel
} from '$lib/models/IPolygonModel';
import type { IPolylinePropertyBagModel } from '$lib/models/IPolylinePropertyBagModel';
import api from '$lib/services/kyAuthenticatedInstance';
import { tabletModeStore } from '$lib/stores/tablet-mode-store';
import { hexToRgb } from '$lib/utils/utils';
import { point } from '@turf/helpers';
import midpoint from '@turf/midpoint';
import { v4 as uuidv4 } from 'uuid';
import {
	ArcType,
	BillboardGraphics,
	Camera,
	Cartesian2,
	Cartesian3,
	Cartographic,
	Color,
	ColorMaterialProperty,
	ConstantPositionProperty,
	CustomDataSource,
	CzmlDataSource,
	EllipseGeometry,
	EllipsoidGeodesic,
	Entity,
	EntityCollection,
	GeometryInstance,
	GroundPrimitive,
	HeadingPitchRange,
	HeightReference,
	JulianDate,
	LabelGraphics,
	Material,
	MaterialAppearance,
	Math as CesiumMath,
	NearFarScalar,
	PerspectiveOffCenterFrustum,
	PointGraphics,
	PolygonGraphics,
	PolygonHierarchy,
	PolylineDashMaterialProperty,
	PolylineGraphics,
	PropertyBag,
	Quaternion,
	RectangleGraphics,
	RequestErrorEvent,
	sampleTerrainMostDetailed,
	Viewer,
	BoxGraphics,
	DataSource,
	Rectangle,
	DistanceDisplayCondition,
	ClassificationType,
	Ray,
	defined,
	VerticalOrigin,
	HorizontalOrigin,
	CallbackProperty,
	Matrix4,
	CorridorGraphics,
	Ellipsoid,
    Transforms
} from 'cesium';
import { HTTPError } from 'ky';
//@ts-ignore
import isPointInPolygon from 'point-in-polygon';
import { get } from 'svelte/store';
import {
	handleUnauthorizedResourceFetch,
	useDataSources
} from './cesium-datasources';
import { busyIndicator, busyStateMessage, customTerrainOffsets, mapSettingsState, useCesiumViewer } from './cesium-viewer';
import { mapToolsStore } from './map-tools-store';
import { protractorBase64 } from './protractor-base64';
import { mapModeStore } from './map-mode-store';
import {
	dataSourceColorPicker,
	entityColorPicker,
	findEntityByIdBetweenDataSources,
	processPlanCzml,
	toggleAviationObstaclesReferences
} from './sandbox-utils';
import type { ISupportModel } from '$lib/models/ISupportModel';
import type { ContextualizedCzmlResponse, CzmlResponse } from '$lib/models/IProjectModel';
import { getEntityAreaToLoad } from './map-event-listeners';
import {
	areaModeState,
	markSectionCalculationCacheStale
} from './maptools/area-mode-state.svelte';
import { clearLoadCases } from './maptools/areaToolLoadCases.svelte';
import { mapLayersStore } from './map-layers-store';
import type { IAviationObstaclePropertyBagModel } from '$lib/models/IAviationObstacle';
import type { ICableModel } from '$lib/models/ICableModel';
import activeAreaAbortController from '$lib/stores/query-stores';
import { toast } from '$lib/components/organisms/toast/toast';
import { getSelectedSpan } from '$lib/contexts/lines-context.svelte';
import { ENTITY_ID_PREFIX } from '$lib/utils/constants';
import { fetchAndApplySectionInfo } from './maptools/area-mode-effects.svelte';

/* Models and Types */
const EntityTypes = {
	AVIATION_OBSTACLE: 'AVIATION_OBSTACLE',
	POLE: 'POLE',
	LINE: 'LINE',
	SPAN: 'SPAN',
	TEMPORARY_LINE: 'TEMPORARY_LINE',
	SUPPORT: 'SUPPORT',
	CROSSBEAM: 'CROSSBEAM',
	UTILITYLINK: 'UTILITYLINK',
	CABLE_PATH: 'CABLE_PATH',
	TRENCH_PATH: 'TRENCH_PATH',
	POLYGON_PART: 'POLYGON_PART',
	POLYGON_SHAPE: 'POLYGON_SHAPE',
	POLYGON_LABEL: 'POLYGON_LABEL',
	POLYLINE_VOLUME: 'POLYLINE_VOLUME',
	PROJECT_POLYGON_PART: 'PROJECT_POLYGON_PART',
	PROJECT_POLYGON_SHAPE: 'PROJECT_POLYGON_SHAPE',
	PROJECT_POLYGON_LABEL: 'PROJECT_POLYGON_LABEL',
	PROJECT_POLYGON: 'PROJECT_POLYGON',
	MASTPOINT: 'MASTPOINT',
	DISTANCE_MEASURE_LINE_PART: 'DISTANCE_MEASURE_LINE_PART',
	SANDBOX_POLYLINE_PART: 'SANDBOX_POLYLINE_PART',
	SANDBOX_POLYLINE: 'SANDBOX_POLYLINE'
} as const;

let currentlyLoadingAreaCenter: Cartographic | null;
let prevSceneAreaCenter: Cartographic | null;

const entityIsPartOfMastpoint = (entityPropertyBag: PropertyBagEntityTypes) => {
    if (!entityPropertyBag) {
        return false;
    } else if (
        entityPropertyBag.entityType === EntityTypes.POLE ||
        entityPropertyBag.entityType === EntityTypes.CROSSBEAM ||
        entityPropertyBag.entityType === EntityTypes.UTILITYLINK ||
        entityPropertyBag.entityType === EntityTypes.SUPPORT
    ) {
        return true;
    }
    return false;
};

/**
 * Fetch map metadata within the constraints of the authenticated user's company (if registered).
 * 
 * @param projectId - Not in use. Optional project ID which must be passed if loading data for a specific project
 */
const fetchMapData = async ({
	signal,
	planId,
	externalMastPointId
}: {
	signal?: AbortSignal;
	planId?: number | string | null | undefined;
	externalMastPointId?: string | null;
}): Promise<void> => {
	const { rectangleAreaDataSource } = useDataSources();
	const viewer = useCesiumViewer();
	const polygonHashMap: Map<
		string,
		{
			id: string;
			entities: any[];
			active: boolean;
		}
	> = new Map();
	try {
		let mapDataCZML: { [key: string]: any } = {};
		mapToolsStore.resetMapToolsStore(viewer);
		if (externalMastPointId) {
			busyStateMessage.set(`Laster kartinnhold for mastepunkt ${externalMastPointId}`);
			mapDataCZML = await api.get(`MastPoint/${externalMastPointId}`, { signal }).json();
		}

		if (
			!externalMastPointId &&
			(!planId ||
				planId === null ||
				planId === undefined ||
				typeof planId === 'undefined')
		) {
			busyStateMessage.set('Laster ned kartinnhold...');
			//mapDataCZML = await api.get(`MastPoint/czml/minimized`, { signal }).json();
			mapDataCZML = await api.get(`PolygonExports`, { signal }).json();
			busyStateMessage.set('Prosesserer data...');
			if (mapDataCZML) {
				for( let polygonLngLat in mapDataCZML){
					if (mapDataCZML[polygonLngLat].id === 'document') return;
					//Should be revised when backend serves lat/lng in an array instead of hashmap
					const polygonData: any[] = mapDataCZML[polygonLngLat];
					const areaId: string = uuidv4();

					polygonHashMap.set(polygonLngLat, { id: areaId, entities: polygonData, active: false });

					//Each area covers 11.1km (rounded first decimal in lat/lng);
					const [lng, lat] = polygonLngLat.split(',').map((radians) => parseFloat(radians));
					const rectangleArea = Rectangle.fromRadians(
						((lng - 0.05) * Math.PI) / 180,
						((lat - 0.05) * Math.PI) / 180,
						((lng + 0.05) * Math.PI) / 180,
						((lat + 0.05) * Math.PI) / 180
					);
					//Mer performant med Rectangle Primitive?
					const rectangleEntity = new Entity({
						id: 'AreaRectangle:' + polygonLngLat,
						rectangle: {
							classificationType: ClassificationType.TERRAIN,
							coordinates: rectangleArea,
							material: new ColorMaterialProperty(Color.WHITE.withAlpha(0.7)),
							distanceDisplayCondition: new DistanceDisplayCondition(30000.0)
						}
					});
					rectangleAreaDataSource.entities.add(rectangleEntity);
				}
			}
		}
		viewer.dataSources.add(rectangleAreaDataSource);
		busyStateMessage.set('');
	} catch (error) {
		if (error instanceof RequestErrorEvent) {
			handleUnauthorizedResourceFetch(error);
		} else if (error instanceof HTTPError) {
			// Ky
			toast({
				type: 'error',
				title: 'Feil ved innlasting av kartdata',
				description: `Kartdata kunne ikke lastes inn. Vennligst prøv igjen senere. Respons: ${error.message}`
			});
			console.error(error.message);
		}
	}
};

/**
 * Creates and returns a polygon entity.
 * 
 * @param polygonId The string id of the entity
 * @param positions Cartesian 3 coordinates
 * @param color Optional color (Default Purple)
 * @returns Entity
 * 
 */
const createPolygonEntity = (polygonId: string, positions: Cartesian3[], opacity: number = 0.01, color: Color = Color.PURPLE) => {
	const polygonEntity = new Entity({
		id: polygonId,
		// @ts-ignore - Custom SmartGIS entity type
		type: EntityTypes.POLYGON_SHAPE,
		properties: new PropertyBag({
			entityType: EntityTypes.POLYGON_SHAPE
		}),
		polygon: new PolygonGraphics({
			// @ts-ignore - Supposed to be like this
			hierarchy: positions,
			heightReference: HeightReference.CLAMP_TO_GROUND,
			material: color.withAlpha(opacity)
		})
	});

	return polygonEntity;
}

const createRectangleReference = (rId: 'activeArea' | 'loadingArea', coordinates: {latitude: number, longitude: number}, size: number) => {
	const { rectangleAreaDataSource } = useDataSources();
	const rectangleEntity = new Entity({
		id: rId,
		rectangle: {
			coordinates: Rectangle.fromCartesianArray(
				createCartesianAreaByLngLatCenter(
					coordinates.longitude,
					coordinates.latitude,
					size
				)
			),
			material: rId === 'activeArea' ?  new ColorMaterialProperty(Color.LIGHTGREEN.withAlpha(0.3)) : new ColorMaterialProperty(Color.GOLD.withAlpha(0.3)),
			distanceDisplayCondition: new DistanceDisplayCondition(30000.0)
		}
	});
	if (defined(rectangleAreaDataSource.entities.getById(rId)))
		rectangleAreaDataSource.entities.removeById(rId);

	rectangleAreaDataSource.entities.add(rectangleEntity);
}

const isCameraOutsideBounds = (sceneAreaCenter: Cartographic, lengthFromCenter: number) => {
    if (!prevSceneAreaCenter && !currentlyLoadingAreaCenter) {
		// First fetch only. Neither previous area nor current area exists. There are no bounds.
        return true;
    }

    const threshold = lengthFromCenter - lengthFromCenter * 0.05;
    const isOutsideBounds = (center: Cartographic | null | undefined) => {
        if (!center) return true;
        const distance = distanceBetweenTwoCartographicPoints(sceneAreaCenter, center);
        return distance > threshold;
    };

    const outsideLoadingArea = isOutsideBounds(currentlyLoadingAreaCenter);
    const outsidePreviousArea = isOutsideBounds(prevSceneAreaCenter);

	if(!outsidePreviousArea && outsideLoadingArea){
		// Flush abort controller and reset currenylyLoadingAreaCenter
		const abortController = get(activeAreaAbortController);
		if(abortController){
			abortController.abort(new DOMException('Request aborted by re-initiating query.'));
			activeAreaAbortController.set(undefined);
			currentlyLoadingAreaCenter = null;
		}
	}
    // When outside both areas, reload is true.
    return outsideLoadingArea && outsidePreviousArea;
};

/**
 * Force a manual reload of all entities in the current viewport. Mirrors the
 * "Oppdater kartdata manuelt" button in MapSettings — extracted so other UI
 * (e.g. the area-mode stale-data overlay) can trigger the same flow.
 */
const runManualMapRefresh = async (): Promise<void> => {
	busyIndicator.loadingData();
	await fetchEntitiesInArea({ sceneCenter: undefined, reloadArea: true });
};

/**
 * fetchEntitiesInArea uses a coordinate to load entities in a specified area. The function accepts a coordinate which is the center of the area to load, and an optional boolean which forces a reload of the area.
 * This function is also used in the camera.moveEnd eventlistener and is used in alle cases where mapdata should be refreshed.
 *
 * @param sceneCenter Object: { longitude: number, latitude: number} Contains coordinates which marks current scene centerpoint.
 * @param reloadArea Boolean. Hard refresh. Determines wether an area should be reloaded even if it is within bounds of previously loaded area. Optional and defaults to false.
 *
 * @returns void
 */
const fetchEntitiesInArea = async ({
	sceneCenter,
	reloadArea = false
}: {
	sceneCenter?: {
		longitude: number;
		latitude: number;
	};
	reloadArea: boolean;
}) => {
	const viewer = useCesiumViewer();
	const { mapdataDataSource, aviationObstacleDataSource, rectangleAreaDataSource, distanceAnalysisTerrainProfilesDataSource, distanceAnalysisPolylinesDataSource } =
		useDataSources();
	
	const requestId = Math.random() * 100;
	// If Scene center is not provided and reloadArea is false, return early.
	if (!sceneCenter && !reloadArea) {
		return;
	}
	// However if reloadArea is true, force reload
	if (!sceneCenter && reloadArea) {
		prevSceneAreaCenter = null;
		const newViewportCapture = getEntityAreaToLoad(viewer);

		if (
			!newViewportCapture ||
			!newViewportCapture.length ||
			newViewportCapture.some((i) => isNaN(i))
		) {
			
			console.log(requestId, "early return no entity area");
			return;
		}
		sceneCenter = {
			longitude: newViewportCapture[0],
			latitude: newViewportCapture[1]
		};
	}
	
	// If coordinates are not fully provided, return early
	if (!sceneCenter?.longitude || !sceneCenter?.latitude) {
		// Could not retrieve lat/lng
		toast({
			type: 'warning',
			title: 'Feil ved innlasting av område',
			description: 'Kunne ikke hente kartkoordinater. Vennligst beveg deg i kartet for å prøve på nytt.'
		});
		return;
	}

	const sceneAreaCenter = Cartographic.fromDegrees(sceneCenter.longitude, sceneCenter.latitude);

	// We call this a radius, but it is actually a rectangle created from the centerpoint and the length from center. 
	const lengthFromCenter = get(mapModeStore).settings.entityArea;
	// Checks different conditions and returns a boolean wether the area should be reloaded or not.
	const cameraOutsideBounds = isCameraOutsideBounds(sceneAreaCenter, lengthFromCenter);
	
	// Proceed with loading data. Checks are complete
	if (cameraOutsideBounds || reloadArea) {
		const existingAbortController = get(activeAreaAbortController);
		// Check if another query is pending, and signal abort.
		if(existingAbortController){
			existingAbortController.abort(new DOMException('Request aborted by re-initiating query.'));
			activeAreaAbortController.set(undefined);
		}
			
		currentlyLoadingAreaCenter = sceneAreaCenter;
		createRectangleReference('loadingArea', sceneCenter, lengthFromCenter);

		const displayedDataSources = get(mapModeStore).displayedDataSources;
		const planIds = displayedDataSources.map((plan) => {
			return parseInt(plan.split(':')[1]);
		});

		const includeAviationObstacles = get(mapLayersStore).aviationObstaclesBlueDisplay ||
		get(mapLayersStore).aviationObstaclesRedDisplay;
		const distanceAnalysisModeActive = get(mapToolsStore).mapStoreInputMode === 'distanceMode';

		const parameters = {
			distanceParam: lengthFromCenter,
			planId: planIds[0],
			centerLatLng: {
				lat: sceneCenter.latitude,
				lng: sceneCenter.longitude
			}
		}

		const {distanceParam, ...body} = parameters;
		const queryObject = {AviationObstacles: includeAviationObstacles, DistanceControlLineTerrain: distanceAnalysisModeActive};

		try {
			// Create a new AbortController instance for this request
			activeAreaAbortController.set(new AbortController());
			// Get the abortController's signal
			const abortController = get(activeAreaAbortController);
			
			// Fetch entity area
			const fetchedEntityArea: ContextualizedCzmlResponse = await api.post('PolygonExports/czml/' + distanceParam + '?czmlResponseOptions=' + JSON.stringify(queryObject), {
				json: body,
				signal: abortController ? abortController.signal : undefined 
			}).json();
			
			// Fetch success, flush abort controller and reset currenylyLoadingAreaCenter
			if(abortController){
				activeAreaAbortController.set(undefined);
				currentlyLoadingAreaCenter = null;
			}

			// Set previous area to sceneArea if we have rendered something new. If not, we'll keep our old reference
			prevSceneAreaCenter = sceneAreaCenter;
			createRectangleReference('activeArea', sceneCenter, lengthFromCenter);

			// Remove temprorary rectangle, indicating loading.
			if (defined(rectangleAreaDataSource.entities.getById('loadingArea')))
				rectangleAreaDataSource.entities.removeById('loadingArea');

			// Remove all processed entities when fetching a new area
			mapdataDataSource.entities.removeAll();
			displayedDataSources.forEach((planName) => {
				viewer.dataSources.getByName(planName).forEach((datasource) => {
					const planPolygon = datasource.entities.getById(
						`sandbox_polygon:${datasource.name.split(':')[1]}`
					);
					const projectPolygon = datasource.entities.values.find( entity =>
						entity.id.startsWith(`ProjectPolygon:`)
					);
					datasource.entities.removeAll();
					// We need to preserve our polygon, as it is not affected by area loading.
					// May need to move plan polygons to another datasource in the future.
					if (planPolygon) datasource.entities.add(planPolygon);
					if (projectPolygon) datasource.entities.add(projectPolygon);
				});
			});

			// Clear all optional datasources when loading a new area
			if(fetchedEntityArea.aviationObstacles?.length > 1) aviationObstacleDataSource.entities.removeAll();
			if(fetchedEntityArea.terrainProfiles?.length > 1) distanceAnalysisTerrainProfilesDataSource.entities.removeAll();
			if(fetchedEntityArea.lineTerrainDistance?.length > 1) distanceAnalysisPolylinesDataSource.entities.removeAll();

			// Read-only pan-fetch — entities don't change, so don't trigger the
			// area-mode /info refresh hook (would gratuitously wipe section info
			// every time the camera moves while a polygon is drawn).
			await handleContextualizedCzmlResponse(fetchedEntityArea, {
				skipAreaModeRefresh: true
			});

			if (defined(rectangleAreaDataSource.entities.getById('loadingArea')))
				rectangleAreaDataSource.entities.removeById('loadingArea');

            // Area refetch renders/patches every entity in the datasource. Makes entity states set by info section undone.
            // Most reliable way to ensure the correct view, is to fetch the section info after entities are added to the map.
            // A full refresh.
            if(areaModeState.active && areaModeState.polygon){
                fetchAndApplySectionInfo();
            }
		} catch(error) {
			if (defined(rectangleAreaDataSource.entities.getById('loadingArea')))
				rectangleAreaDataSource.entities.removeById('loadingArea');
			if (error instanceof RequestErrorEvent) {
				handleUnauthorizedResourceFetch(error);
			} else if (error instanceof HTTPError) {
				// Ky
				toast({
					type: 'warning',
					title: 'Innlastingsfeil',
					description: 'Entiteter på en eller flere planer kunne ikke lastes inn. Vennligst prøv igjen senere.'
				});
				console.error(error.message);
			} else if(error instanceof DOMException){
				// If there is no abortController, noe area is fetching. Set indicator to idle
				console.warn(error, 'Innlasting avbrutt av bruker');
			} else {
				console.error(error);
			}
		}
	} 
};

const createTerrainOffsets = async () => {

	const { distanceAnalysisTerrainProfilesDataSource } = useDataSources();
	const originalOffsets = get(mapToolsStore).distanceAnalysisSettings.terrainProfileOffsets;
	const customOffsets = get(customTerrainOffsets);
	const offsets = [...originalOffsets, ...customOffsets];

	const entities = distanceAnalysisTerrainProfilesDataSource.entities.values;
	// Step 1: Remove previously generated offset entities
	const offsetEntities = entities.filter((e) => e.id.startsWith('offset'));
	offsetEntities.forEach((e) => distanceAnalysisTerrainProfilesDataSource.entities.remove(e));
  
	// Step 2: Prepare active offsets once
	const activeOffsets = offsets.filter((o) => o.active);
  
	// Early exit if no offsets active
	if (activeOffsets.length === 0) return;
  
	// Step 3: Iterate once and clone per offset
	for (const entity of entities) {
	  if (entity.id.startsWith('offset') || !entity.polyline?.positions || !entity.show) continue;
  
	  const basePositions = entity.polyline.positions.getValue();
	  if (!basePositions) continue;
  
	  for (const { offsetInMeters, color } of activeOffsets) {
		const modifiedPositions = basePositions.map((coord: Cartesian3) => {
		  const magnitude = Cartesian3.magnitude(coord);
		  const scalar = (magnitude + offsetInMeters) / magnitude;
		  return Cartesian3.multiplyByScalar(coord, scalar, new Cartesian3());
		});
  
		const offsetEntity = new Entity({
		  id: `offset-${offsetInMeters}-${entity.id}`,
		  name: entity.name,
		  polyline: new PolylineGraphics({
			positions: modifiedPositions,
			width: entity.polyline.width,
			material: new ColorMaterialProperty(Color.fromBytes(...color)),
			// Area-mode keeps measurements visible at any zoom; the 200 m cap is
			// only for the global distance view.
			distanceDisplayCondition: areaModeState.active
				? undefined
				: new DistanceDisplayCondition(0.0, 200.0),
		  }),
		});
  
		distanceAnalysisTerrainProfilesDataSource.entities.add(offsetEntity);
	  }
	}
}

/**
 * Handles a response directly from the API with a contextualized model.
 * e.g.: liveObject, plans, aviationObstacles, etc.
 * 
 * @param response The API response 
 */
/**
 * Renders a contextualised CZML response onto the Cesium viewer (live + plan
 * datasources, aviation obstacles, terrain profiles, line-terrain distances).
 *
 * **Area-mode side-effect (default ON, gated by overlap):** when a polygon
 * is drawn, mutations to mastpoints / lines / damages / etc. *may* invalidate
 * the cached section info computed against the previous entity state — but
 * only if the mutated entity is actually inside the polygon. After applying
 * the CZML, this function scans the response packets for mast and line IDs
 * and compares them against the cached `sectionInfo.masts` and the flattened
 * `sectionCircuits[].spanLinesets[].lines`. If there is no overlap, the
 * refresh is skipped. If there is overlap, it clears the loadcase
 * datasources, nulls out `areaModeState.sectionInfo` — which triggers the
 * auto-fetch reactor in `area-mode-effects.svelte.ts` to re-issue `/info`
 * against the current polygon — and marks the persisted section-calculation
 * report stale so the `Generer på nytt` banner appears in any open report
 * tab.
 *
 * The overlap check is *strict*: an unmatched response leaves the cache
 * untouched. This relies on the backend's convention of always echoing the
 * affected mastpoint when a child (pole, cable, support, damage) changes, so
 * a parent-only match is sufficient.
 *
 * Mutation onSuccess handlers therefore get this refresh **automatically** by
 * piping their response through this function (which they already do). New
 * mutation endpoints under `MastPoint/`, `Lines/`, `Damages/`, etc. only need
 * to follow that existing convention.
 *
 * **Opt out:** read-only paths that don't change entities (notably the
 * pan-the-map auto-loader `fetchEntitiesInArea`) pass
 * `{ skipAreaModeRefresh: true }` so they don't gratuitously reissue `/info`.
 */
const handleContextualizedCzmlResponse = async (
	response: ContextualizedCzmlResponse,
	options: { skipAreaModeRefresh?: boolean } = {}
) => {
	const viewer = useCesiumViewer();
	const { 
		mapdataDataSource, 
		aviationObstacleDataSource, 
		distanceAnalysisPolylinesDataSource, 
		distanceAnalysisTerrainProfilesDataSource 
	} = useDataSources();
	
	const {aviationObstacles, lineTerrainDistance, terrainProfiles, plan, liveObjects} = response;
	// Live is rendered first to make sure all references are available
	await mapdataDataSource.process(liveObjects);

	// Handle aviation obstacles
	if(aviationObstacles && aviationObstacles.length > 1){
		if(!viewer.dataSources.contains(aviationObstacleDataSource)){
			await viewer.dataSources.add(aviationObstacleDataSource);
		}
		await aviationObstacleDataSource.process(aviationObstacles);
	}
	// Handle line distance analysis objects
	if(lineTerrainDistance && lineTerrainDistance.length > 1){
		if(!viewer.dataSources.contains(distanceAnalysisPolylinesDataSource)){
			await viewer.dataSources.add(distanceAnalysisPolylinesDataSource);
		}
		await distanceAnalysisPolylinesDataSource.process(lineTerrainDistance);
	}
	// Handle terrain profiles
	if(terrainProfiles && terrainProfiles.length > 1){
		if(!viewer.dataSources.contains(distanceAnalysisTerrainProfilesDataSource)){
			await viewer.dataSources.add(distanceAnalysisTerrainProfilesDataSource);
		}
		await distanceAnalysisTerrainProfilesDataSource.process(terrainProfiles);
		if(!plan){
			// Create offsets if we are not in plan mode. Else, handle this after planobjects have been processed.
			await createTerrainOffsets();
		}
	}

	// Handle plans
	if (plan) {
		await processPlanCzml(plan.planObjects).then(() => {
			redrawOperationalMarkingsIfEnabled();
		});
	} else if(aviationObstacles && aviationObstacles.length > 1){
		// Should only trigger if in live and with active Aviation obstacles
		toggleAviationObstaclesReferences();
	}else {
		redrawOperationalMarkingsIfEnabled();
	}
	// Finally pick colors for all data sources
	await dataSourceColorPicker(mapdataDataSource);
	const planDataSource = get(mapModeStore).activeDataSource;
	if (planDataSource) {
		await dataSourceColorPicker(planDataSource);
	}

	// Area-mode auto-refresh. See the JSDoc on this function for the rationale
	// and the opt-out flag used by the panning auto-loader.
	//
	// Strict overlap gate: only refresh when at least one mast or line in the
	// CZML response is also present in the cached section info. The backend
	// always echoes the affected mastpoint when a child (pole, cable, support,
	// damage) changes, so a parent-only match is enough — we never need to
	// match against the child entity itself.
	if (
		!options.skipAreaModeRefresh &&
		areaModeState.active &&
		areaModeState.polygon &&
		contextualizedCzmlOverlapsSection(response)
	) {
		clearLoadCases();
		areaModeState.sectionInfo = null;
		areaModeState.sectionInfoError = null;
		areaModeState.sectionInfoLoading = false;
		areaModeState.activeLoadCases = [];
		await markSectionCalculationCacheStale();
	}
}

/**
 * Returns true when any mast or line referenced in a CZML response is present
 * in the currently cached section info (`areaModeState.sectionInfo`).
 *
 * Shared by the area-mode auto-refresh gate in `handleContextualizedCzmlResponse`
 * and by the cross-tab CZML broadcast receiver in `area-mode-effects`. The
 * latter matters when several Cesium tabs are open: only the tab whose polygon
 * actually contains the changed entities should react to a broadcast.
 *
 * Relies on the backend convention of always echoing the affected mastpoint
 * when a child (pole, cable, support, damage) changes, so a parent-only match
 * is sufficient — we never need to match against the child entity itself.
 */
export const contextualizedCzmlOverlapsSection = (
	response: ContextualizedCzmlResponse
): boolean => {
	if (!areaModeState.sectionInfo) return false;

	const affectedMasts = new Set<number>();
	const affectedLines = new Set<number>();
	const packets: Array<{ id?: unknown }> = [
		...(response.liveObjects ?? []),
		...(response.plan?.planObjects ?? [])
	];
	for (const p of packets) {
		if (typeof p?.id !== 'string') continue;
		if (p.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT)) {
			const n = Number(p.id.slice(ENTITY_ID_PREFIX.MASTPOINT.length));
			if (Number.isFinite(n)) affectedMasts.add(n);
		} else if (p.id.startsWith(ENTITY_ID_PREFIX.LINE)) {
			const n = Number(p.id.slice(ENTITY_ID_PREFIX.LINE.length));
			if (Number.isFinite(n)) affectedLines.add(n);
		}
	}

	const cachedMasts = new Set(areaModeState.sectionInfo.masts.map((m) => m.id));
	const cachedLines = new Set<number>();
	for (const sc of areaModeState.sectionInfo.sectionCircuits ?? []) {
		for (const span of sc.spanLinesets ?? []) {
			for (const ln of span.lines ?? []) cachedLines.add(ln.id);
		}
	}

	return (
		[...affectedMasts].some((id) => cachedMasts.has(id)) ||
		[...affectedLines].some((id) => cachedLines.has(id))
	);
};

const redrawOperationalMarkingsIfEnabled = () => {
	const { operationalMarkingDatasource } = useDataSources();

	const operationalMarkingEnabled = get(mapToolsStore).displayOperationalMarkingLabels;
	if (operationalMarkingEnabled) {
		mapToolsStore.update((store) => {
			store.displayOperationalMarkingLabels = !operationalMarkingEnabled;
			return store;
		});
		operationalMarkingDatasource.entities.removeAll();
		toggleOperationalMarkingLabels();
	}
};

/* Reusable utils functions */
const cartesianToLatitudeLongitude = (
	cartesian3: Cartesian3
): { latitude: number; longitude: number } => {
	const viewer = useCesiumViewer();
	const cartographic = viewer.scene.globe.ellipsoid.cartesianToCartographic(cartesian3);

	const longitude = CesiumMath.toDegrees(cartographic.longitude).toFixed(10);

	const latitude = CesiumMath.toDegrees(cartographic.latitude).toFixed(10);

	return {
		latitude: parseFloat(latitude),
		longitude: parseFloat(longitude)
	};
};

function getPolygonBoundariesAsLatLngs(polygon: Cartesian3[]): [number, number][] {
	return polygon.map((point: Cartesian3) => {
		const { latitude, longitude } = cartesianToLatitudeLongitude(
			new Cartesian3(point.x, point.y, point.z)
		);
		return [longitude, latitude];
	});
}

export type PropertyBagEntityTypes = | ILinePropertyBagModel
| IDrawnPolygonShape
| IDrawnPolygonPart
| IPolygonLabel
//| IMastPointFullModel.MastPointPropertyBagModel
| IMastPointMinimalModel.MastPointPropertyBagModel
| IMastPointMinimalModel.PolePropertyBagModel
| IDistanceMeasureLinePropertyBagModel
| IPolylinePropertyBagModel
//| ILoadOccurrenceLinePropertyBagModel
| ISupportModel
| IAviationObstaclePropertyBagModel
| ICableModel.CablePropertyBagModel
| ICableModel.CablePathPropertyBagModel;

/**
 * Retrieve the `properties` bag of a specific Cesium Entity
 * @param entity
 */
const getEntityPropertyBag = <
	T extends PropertyBagEntityTypes
>(
	entity: Entity
): T => {
	return entity?.properties?.getValue(new JulianDate()) as T;
};

/**
 * Cesium entities may have properties (PropertyBag), but there is no built-in way to "upsert" (add new OR modify existing) properties.
 * This utility function adds that.
 * @param entity - The Entity onto which a property should be upserted
 * @param propertyName - The property name to upsert a value onto.
 * @param propertyValue - The value to upsert.
 */
const upsertEntityProperty = ({
	entity,
	propertyName,
	propertyValue
}: {
	entity: Entity;
	propertyName: string;
	propertyValue: unknown;
}): Entity => {
	if (entity.properties) {
		if (entity.properties.hasProperty(propertyName)) {
			entity.properties[propertyName] = propertyValue;
		} else {
			entity.properties.addProperty(propertyName, propertyValue);
		}
	} else {
		console.error('Entity has no properties object?:');
	}

	return entity;
};

/**
 * A polygon may reside in a polygon data source (sandbox_polygonDataSource).
 * This function retrieves a single polygon based on a reference ID.
 * @param polygonReferenceId
 */
const getPolygonByPolygonReferenceId = ({
	polygonReferenceId
}: {
	polygonReferenceId: IDrawnPolygonPart['parentPolygonId'];
}) => {
	const { sandbox_polygonDataSource } = useDataSources();
	let polygon: Entity | undefined;

	polygon = sandbox_polygonDataSource.entities.values.find((polygonEntity: Entity) => {
		const entityPropertyBag = getEntityPropertyBag<IDrawnPolygonPart | IDrawnPolygonShape>(
			polygonEntity
		);
		if (
			entityPropertyBag.entityType === EntityTypes.PROJECT_POLYGON_SHAPE ||
			entityPropertyBag.entityType === EntityTypes.POLYGON_SHAPE
		) {
			if (entityPropertyBag.referencePolygonId === polygonReferenceId) {
				return true;
			} else if (polygonEntity.id === polygonReferenceId) {
				return true;
			} else {
				return false;
			}
		} else {
			return false;
		}
	});

	return polygon;
};

const getPolygonEdgesSortedByDistanceToPoint = ({
	polygon,
	point
}: {
	polygon: PolygonHierarchy;
	point: Cartesian3;
}) => {
	const distances: { index: number; distance: number }[] = [];
	for (let i = 0; i < polygon.positions.length; i++) {
		distances.push({
			index: i,
			distance: Cartesian3.distance(point, polygon.positions[i])
		});
	}

	return distances.sort((a, b) => a.distance - b.distance);
};

/**
 * Retrieve an array of Cesium Point entities (selectable edge points for polygon) based on a Cartesian position.
 * @param position
 */
const getPolygonSelectableEdgePointsByPosition = ({
	position
}: {
	position: Cartesian3;
}): Entity[] => {
	const { sandbox_polygonDataSource } = useDataSources();

	const selectableEdgePoint = sandbox_polygonDataSource.entities.values.filter((entity: Entity) => {
		const entityPropertyBag = getEntityPropertyBag<IDrawnPolygonPart | IDrawnPolygonShape>(entity);
		if (
			(entityPropertyBag.entityType === EntityTypes.POLYGON_PART ||
				entityPropertyBag.entityType === EntityTypes.PROJECT_POLYGON_PART) &&
			entity.position
		) {
			return (entity.position.getValue(new JulianDate()) as Cartesian3).equals(position);
		}
		return false;
	});

	return selectableEdgePoint;
};

const doesPolygonContainDuplicateEdges = ({ polygon }: { polygon: PolygonHierarchy }) => {
	const uniqueEdgePositions = new Set(
		polygon.positions.map((v) => {
			return `${v.x},${v.y}`;
		})
	);

	return uniqueEdgePositions.size < polygon.positions.length;
};

function getMapEntitiesWithinBoundaries(mapEntities: Entity[], polygonBoundaries: any[]) {
	return mapEntities.filter((entity: Entity) => {
		// This if check is necessary to avoid polygon which does not contain an entityType and causes an error.
		if((entity.point || entity.cylinder) && entity.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT)){
			const { entityType } = getEntityPropertyBag(entity);
			if (entityType === EntityTypes.MASTPOINT && entity.position) {
				const entityPosition = entity.position.getValue(new JulianDate());
				if (entityPosition) {
					const { x, y, z } = entityPosition;
					const { latitude, longitude } = cartesianToLatitudeLongitude(new Cartesian3(x, y, z));
					if (isPointInPolygon([longitude, latitude], polygonBoundaries) && entity.show) {
						return entity;
					}
				} else {
					console.error('No entityPosition', entityPosition);
				}
			}
		}
	});
}
const computeRectangleWidth = (rect: Rectangle) => {
	let topleftCart3 = Cartesian3.fromRadians(rect.west, rect.north);
	let bottomleftCart3 = Cartesian3.fromRadians(rect.west, rect.south);
	let toprightCart3 = Cartesian3.fromRadians(rect.east, rect.north);

	// converting to length and width:
	let lengthDistance = Cartesian3.distance(topleftCart3, bottomleftCart3);
	let widthDistance = Cartesian3.distance(topleftCart3, toprightCart3);

	return { lengthDistance, widthDistance };
};
/**
 * Creates a bounding box for the polygon Entity and moves the camera to contain its location from a top down view
 * @param polygonEntity 
 */
const flyToPolygonBoundingBox = async (polygonEntity: Entity) => {
	const viewer = useCesiumViewer();
	const polygonPositions: Cartesian3[] = polygonEntity.polygon?.hierarchy?.getValue(
		new JulianDate()
	).positions;

	// Create a rectangle which will work as a bounding box for our polygon
	const polyRectangle = Rectangle.fromCartesianArray(polygonPositions);
	const { longitude: reclon, latitude: reclan } = Rectangle.center(polyRectangle);

	// Convert and get values to calculate camera height
	const cartographicCenter = Cartographic.fromRadians(reclon, reclan);
	const terrainHeight = await sampleTerrainMostDetailed(viewer.terrainProvider, [
		cartographicCenter
	]);
	const { lengthDistance, widthDistance } = computeRectangleWidth(polyRectangle);
	const biggestDistance = lengthDistance > widthDistance ? lengthDistance : widthDistance;
	const cameraHeight = terrainHeight[0].height + biggestDistance * 1.8;

	// Get centerpoint of rectangle which we will fly to
	const cartCenter = Cartesian3.fromRadians(reclon, reclan, cameraHeight);

	viewer.camera.flyTo({
		destination: cartCenter,
		duration: 0,
		endTransform: Matrix4.IDENTITY
	});
}

/**
 * Generic function to move (fly) the map view to a specific location.
 * @param latitude
 * @param longitude
 * @param height - zoom level above map
 * @param duration - how long (in ms) should it take to move (fly) to the new location (default 0ms)
 */
function flyToLatLngLocation({
	latitude,
	longitude,
	height,
	duration = 0
}: {
	latitude: number;
	longitude: number;
	height: number;
	duration?: number;
}) {
	const viewer = useCesiumViewer();
	const destination = Cartesian3.fromDegrees(longitude, latitude, height);

	viewer.camera.flyTo({ destination, duration });
}

/**
 * Generic function to move (fly) the map view to a specific location. (Copy of flyToLatLngLocation).
 * The purpose of this function is to move to a specific entity and show it from a panned angle.
 * @param entity - The entity to target
 */
function flyToEntityLocationView({ entity }: { entity: Entity }) {
	const viewer = useCesiumViewer();

	viewer.flyTo(entity, {
		duration: 0,
		offset: new HeadingPitchRange(0, -CesiumMath.PI / 6 + 0.0000001, 75)
	});
}

/**
 * Handles the selection of a specific Cesium Entity based on ID and entity type, and requests a "flyTo" (map move) to the location of the entity
 * @param id - the unique entity ID from the dataset (e.g., poleId / externalPoleId).
 * @param entityType - Currently only "pole" is accepted. (either `"pole"` or `"line"` depending on the type of Cesium Entity).
 * @param maintainView - Boolean. Maintains camera angle when using flyTo if true. Implemented as a part of the map search field.
 */
async function flyToEntityLocation(
	id: string | number,
	entityType: 'pole' | 'line' | 'MastPoint',
	maintainView: boolean
): Promise<void> {
	const entityId = `${entityType}:${id}`;
	const entity = getCZMLEntityById(entityId);

	let latitude = 0;
	let longitude = 0;

	if (entity) {
		if (entityType === 'pole') {
			// This could be both the full and minimal model, but the minimal model contains all data necessary to perform the flyTo-operation
			const { data: entityData } =
				getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(entity);
			latitude = entityData.lat;
			longitude = entityData.lng;
		} else if (entityType === 'line' && entity.polyline) {
			const positions = entity.polyline.positions!.getValue();
			latitude = positions[1];
			longitude = positions[0];
		} else if (entityType === 'MastPoint') {
			const { data: entityData } =
            getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(entity);
            latitude = entityData.lat;
            longitude = entityData.lng;
		}

		setSelectedEntity(entity);

		if (maintainView) {
			flyToEntityLocationView({
				entity
			});
		} else {
			flyToLatLngLocation({
				latitude,
				longitude,
				height: 1500
			});
		}
	}
}

/**
 * Retrieve a Cesium Entity based on its unique ID
 * @param viewer - the (Cesium) Viewer.
 * @param id - of the (Cesium) Entity
 */
function getEntityById(id: string): Entity | undefined {
	const viewer = useCesiumViewer();
	return viewer.entities.getById(id);
}

/**
 * Retrieve a Cesium Entity based on its unique ID
 * @param viewer - the (Cesium) Viewer.
 * @param id - of the (Cesium) Entity
 */
function getCZMLEntityById(id: string): Entity | undefined {
	const { mapdataDataSource } = useDataSources();
	const activeDataSource = get(mapModeStore).activeDataSource;
	if (activeDataSource && activeDataSource.entities.getById(id)) {
		return activeDataSource.entities.getById(id);
	} else {
		return mapdataDataSource.entities.getById(id);
	}
}

/**
 * Programmatically select a Cesium Entity, or deselect already-selected Entity
 * @param viewer - the (Cesium) Viewer.
 * @param entity - the (Cesium) Entity to be selected, or `undefined` to deselect.
 */
function setSelectedEntity(entity: Entity | undefined): boolean {
	const viewer = useCesiumViewer();
	if (entity || entity === undefined) {
		viewer.selectedEntity = entity;
		return true;
	}

	return false;
}

/**
 * Create a protractor (gradskive) surrounding an Entity
 * @param entity The entity around which the protractor should be displayed
 */
const createProtractor = async (entity: Entity): Promise<void> => {
	const viewer = useCesiumViewer();

	if (entity.position) {
		const entityPosition = entity.position.getValue(new JulianDate());

		if (entityPosition) {
			// The geometry of the protractor (circle due to equal axis)
			const protractorCircleInstance = new GeometryInstance({
				geometry: new EllipseGeometry({
					center: entityPosition,
					semiMinorAxis: 10.0,
					semiMajorAxis: 10.0
				}),
				id: `protractor:${entity.id}`
			});

			// Create the new protractor as a primitive ground instance, allowing it to be draped over terrain.
			viewer.scene.primitives.add(
				new GroundPrimitive({
					geometryInstances: [protractorCircleInstance],
					appearance: new MaterialAppearance({
						material: new Material({
							fabric: {
								type: 'Image',
								uniforms: {
									image: protractorBase64, // Use base64 instead of an image URL to avoid a circle of white flash before the image has been loaded.
									repeat: new Cartesian2(1.0, 1.0),
									alpha: 0.5
								},
								components: {
									// Set the alpha channel for the protractor image
									diffuse: 'texture(image, fract(repeat * materialInput.st)).rgb',
									alpha: 'texture(image, fract(repeat * materialInput.st)).a * alpha'
								}
							},
							translucent: true
						}),
						faceForward: true
					})
				})
			);
		}
	}
};

/**
 * Remove the specified ground primitive
 * @param groundPrimitiveId The Cesium Entity ID of the Entity to be removed
 * @returns Boolean indicating if a primitive was removed
 */
const removeGroundPrimitive = (groundPrimitiveId: string): boolean => {
	const viewer = useCesiumViewer();
	let removed = false;
	// Find the ground primitive and remove it from the primitives collection
	for (let i = 0; i < viewer.scene.primitives.length; i++) {
		const primitive = viewer.scene.primitives.get(i);
		if (primitive instanceof GroundPrimitive) {
			// @ts-ignore Internal array of keys for the given ground primitive.
			if (primitive._boundingSpheresKeys.includes(groundPrimitiveId)) {
				removed = viewer.scene.primitives.remove(primitive);
				break;
			}
		}
	}

	return removed;
};

/**
 * Remove all protractors from the primitives collections
 * 
 * @returns Boolean indicating if any protractor was removed
 */
const removeAllProtractors = (): void => {
	const viewer = useCesiumViewer();

	for (let i = 0; i < viewer.scene.primitives.length; i++) {
		const primitive = viewer.scene.primitives.get(i);

		// The protractor should always be a groundprimitive (as opposed to, e.g., a 3DTile layer)
		if (!(primitive instanceof GroundPrimitive)) {
			continue;
		}

		// @ts-ignore Internal array of keys for the given ground primitive.
		if (primitive._boundingSpheresKeys.some((key) => key.includes('protractor:'))) {
			viewer.scene.primitives.remove(primitive);
		}
	}
};
/**
 * Remove all protractors from the primitives collections and create a new protractor for the given entity
 * Makes sure there is always only one protractor visible at a time, and that it is updated to the current selected entity.
 * @param entity 
 */
const reloadProtractor = (entity: Entity): void => {
    removeAllProtractors();
    createProtractor(entity);
}

/**
 * This function is used by the terrain profile tool to visualize the path (line) on the terrain between the user's selected entities.
 * Every change to the `entities` array will remove the existing polyline and redraw the entire path anew.
 * @param ({entities: Entity[]}) - the entities between which the path (line) will be drawn.
 */
const createTerrainProfilePolylineBetweenEntities = async ({
	entities,
	dataSource,
	polylineWidth
}: {
	entities: Entity[];
	dataSource: DataSource;
	polylineWidth: number;
}) => {
	if (entities.length === 0) {
		dataSource.entities.removeAll();
		return;
	}

	// Retrieve positions for all selected entities
	const selectedEntityPositions = entities
		.filter((entity) => entity.position)
		.map((entity) => entity.position!.getValue(new JulianDate()));

	dataSource.entities.suspendEvents();

	dataSource.entities.removeAll();
	dataSource.entities.add({
		polyline: new PolylineGraphics({
			positions: selectedEntityPositions as Cartesian3[],
			width: polylineWidth,
			material: new PolylineDashMaterialProperty({
				color: Color.YELLOW,
				dashPattern: parseInt('1010101010101010', 2)
			}),
			arcType: ArcType.RHUMB,
			clampToGround: true
		})
	});

	dataSource.entities.resumeEvents();
};

/**
 * An entity can have (for now) two states: Selected and unselected.
 * Use this function to toggle the visual clue of whether the entity is selected or unselected
 * @param entities A list of Entity[]
 * @param selected Selected (true) or not/deselected (false)
 */
const toggleEntityVisuals = (entities: Entity[], selected: boolean): void => {
	const {showPartnerEntities, partnerCompanies, enablePartnerColors} = get(mapSettingsState);

	if (selected) {
		entities.forEach((entity: Entity) => {
			if (entity.cylinder) {
				entity.cylinder.material = new ColorMaterialProperty(Color.RED);
			} else if (entity.polyline) {
				entity.polyline.material = new ColorMaterialProperty(Color.RED);
			} else if (entity.point) {
				// @ts-ignore Documented way of changing color
				entity.point.outlineColor = Color.RED;
			}
		});
	} else {
		entities.forEach((entity: Entity) => {
			if (entity.cylinder || entity.polyline) {
				entityColorPicker(entity, partnerCompanies, enablePartnerColors, showPartnerEntities);
				
			} else if (entity.point) {
				// @ts-ignore Documented way of changing color
				entity.point.outlineColor = Color.BLUE;
			}
		});
	}
};

/**
 * GIT blame: Morten
 * It is more appropriate to keep this function seperated from other "ToggleEntityVisuals" as
 * there are some special rules for selecting lines for editing. Several and mixed entities
 * should be highlighted and there is a dedicated store tied to it all.
 * @param entities A list of Entity[]
 * @param selected Selected (true) or not/deselected (false)
 * @param dataSource DataSource to use
 */
const toggleEntityVisualsForLineEditing = (
	entities: Entity[],
	selected: boolean,
	dataSource: CzmlDataSource
): void => {
	const storedSpan = getSelectedSpan();
	const {showPartnerEntities, partnerCompanies, enablePartnerColors} = get(mapSettingsState);
	if(!storedSpan) return;
	
	let mastPointEnd: Entity | null;
	let mastPointStart: Entity | null;

	if(storedSpan.mastpoint1Id !== null){
		mastPointStart = findEntityByIdBetweenDataSources(
			ENTITY_ID_PREFIX.MASTPOINT + storedSpan.mastpoint1Id
		);
	} else {
		mastPointStart = null;
	}
	
	if (storedSpan.mastpoint2Id !== null) {
		mastPointEnd = findEntityByIdBetweenDataSources(
			ENTITY_ID_PREFIX.MASTPOINT + storedSpan.mastpoint2Id
		);
	} else {
		mastPointEnd = null;
	}

    const viewer = useCesiumViewer();
    const {temp_referenceDataSource} = useDataSources();

    if(!viewer.dataSources.contains(temp_referenceDataSource)){
        viewer.dataSources.add(temp_referenceDataSource);
    }

	const markedLineSet: SpanModel.ILineSetDataModel | undefined =
		storedSpan.linesets.find((lineSet: SpanModel.ILineSetDataModel) => {
			return lineSet.lines.some((line) => ENTITY_ID_PREFIX.LINE + line.id === entities[0].id);
		});
	if (selected) {
		if (markedLineSet) {
			markedLineSet.lines.forEach((line) => {
				const entityToVisualize = dataSource.entities.getById(ENTITY_ID_PREFIX.LINE + line.id);
				if (entityToVisualize) {
					const isSelectedInSpan = entityToVisualize.id === entities[0].id;
					const material = setColorProperty(
						isSelectedInSpan ? Color.RED : Color.DARKRED.withAlpha(0.35)
					);
					if (entityToVisualize.polyline) entityToVisualize.polyline.material = material;
				}
			});

            temp_referenceDataSource.entities.removeAll();
            
			if (mastPointStart) {
				//const mastPointToColor = getMastpointWithinContext(mastPointStart);
                const labelEntityStart = new Entity({
                    id: mastPointStart.id + '_label',
                    label: new LabelGraphics({
                        backgroundColor: Color.BLACK,
                        showBackground: true,
                        verticalOrigin: VerticalOrigin.BOTTOM,
                        horizontalOrigin: HorizontalOrigin.CENTER,
                        backgroundPadding: new Cartesian2(10, 6),
                        scaleByDistance: new NearFarScalar(0.0, 1.0, 8.0e6, 0.0),
                        font: '500 14px Poppins',
                        text: 'Spenn start',
                        // I don't know how I came up with this magic number, but it works! (So far)
                        disableDepthTestDistance: 1.2742018*10**7
                    }),
                    position: mastPointStart.position?.getValue()
                });
                temp_referenceDataSource.entities.add(labelEntityStart);
			}
			if (mastPointEnd) {
				//const mastPointToColor = getMastpointWithinContext(mastPointEnd);
                const labelEntityStart = new Entity({
                    id: mastPointEnd.id + '_label',
                    label: new LabelGraphics({
                        backgroundColor: Color.BLACK,
                        showBackground: true,
                        verticalOrigin: VerticalOrigin.BOTTOM,
                        horizontalOrigin: HorizontalOrigin.CENTER,
                        backgroundPadding: new Cartesian2(10, 6),
                        scaleByDistance: new NearFarScalar(0.0, 1.0, 8.0e6, 0.0),
                        font: '500 14px Poppins',
                        text: 'Spenn slutt',
                        // I don't know how I came up with this magic number, but it works! (So far)
                        disableDepthTestDistance: 1.2742018*10**7
                    }),
                    position: mastPointEnd.position?.getValue()
                });
                temp_referenceDataSource.entities.add(labelEntityStart);
			}
		}
	} else {
		let lineSetEntities: (Entity | undefined)[] = [];

		storedSpan.linesets.forEach((lineset) => {
			lineset.lines.forEach(
				(line) =>
					(lineSetEntities = [dataSource.entities.getById(ENTITY_ID_PREFIX.LINE + line.id), ...lineSetEntities])
			);
		});

		const entitiesToUnmark = [mastPointStart, mastPointEnd, ...lineSetEntities, ...entities];
		entitiesToUnmark.forEach((entity: Entity | null | undefined) => {
			if (entity !== null && entity) {
				if (entity.cylinder) {
					const mastPointToColor = getMastpointWithinContext(entity);
					entityColorPicker(mastPointToColor, partnerCompanies, enablePartnerColors, showPartnerEntities);
					
				} else if (entity.polyline) {
					entityColorPicker(entity, partnerCompanies, enablePartnerColors, showPartnerEntities);
				} else if (entity.point) {
					// @ts-ignore Documented way of changing color
					entity.point.outlineColor = Color.BLUE;
				}
			}
		});
        temp_referenceDataSource.entities.removeAll(); 
	}
};

/**
 * GIT blame: Morten
 * A seperate function for toggling entity visuals from within EditLineData.svelte.
 * This function works by updating store and is triggered from the floating sidemenu as
 * opposed to the rest of visuals which are triggered from clicking on map entities.
 * Only updates based on store values.
 */
const toggleEntityVisualsWithinLineSpan = (dataSourceToUse: CzmlDataSource): void => {
	/*const storedSpan = get(lineSpan);
	const selectedLineSet = get(selectedLineGroup) as SpanModel.ILineSetDataModel;
	const {showPartnerEntities, partnerCompanies, enablePartnerColors} = get(mapSettingsState);
	if(!storedSpan) return;

	const markedLineSet: SpanModel.ILineSetDataModel | undefined =
		storedSpan.linesets.find((lineSet: SpanModel.ILineSetDataModel) => {
			return lineSet.id === selectedLineSet?.id;
		});

	const unmarkLineSet: SpanModel.ILineSetDataModel[] | undefined =
		storedSpan.linesets.filter((lineSet: SpanModel.ILineSetDataModel) => {
			return lineSet.id !== selectedLineSet?.id;
		});

	//Unmark every line from other linesets
	unmarkLineSet?.forEach((lineset) => {
		lineset.lines.forEach((line) => {
			const entityToUnmark = dataSourceToUse.entities.getById('line:' + line.id);
			if (entityToUnmark) {
				entityColorPicker(entityToUnmark, partnerCompanies, enablePartnerColors, showPartnerEntities);
			}
		});
	});

	//Mark newly selected lineset from sidemenu
	markedLineSet?.lines.forEach((line) => {
		const entityToVisualize = dataSourceToUse.entities.getById('line:' + line.id);
		if (entityToVisualize) {
			const markedLine = get(selectedLine);
			if(!markedLine){
				toast({
					type: 'warning',
					title: 'Finner ikke ledning',
					description: 'Kan ikke finne valgt ledning i kartet.',
				});
				return; 
			}
			const isSelectedInSpan = entityToVisualize.id === markedLine.id;
			const material = new ColorMaterialProperty(
				isSelectedInSpan ? Color.RED : Color.ORANGERED.withAlpha(0.5)
			);
			if (entityToVisualize.polyline) {
				entityToVisualize.polyline.material = material;
			}
		}
	});*/
};

/**
 * Entities use a property system to detect when something has changed. Suggested solution by
 * Cesium is to create a MateriaProperty with a nested CallbackProperty for entities to react
 * properly to changes in properties.
 * 
 * @param color Color (Cesium.Color)
 * @returns ColorMaterialProperty with a nested CallbackProperty (Cesium.ColorMaterialProperty)
 */
const setColorProperty = (color: Color) => {
	const colorProperty = new ColorMaterialProperty();
	colorProperty.color = new CallbackProperty(() => color, false);
	return colorProperty;
}

const setPartnerColorProperty = (partnerHex: string, opacity: number = 1) => {
	const partnerColor = hexToRgb(partnerHex!, opacity);
	const toColorValues = partnerColor.map((cv, i) => {
		return cv / 255;
	});
	return setColorProperty(new Color(...toColorValues));
}

/**
 * When the user is in a plan context, they may select a line which is LIVE. A LIVE span is always connected to two LIVE
 * Mastpoints even though one of the Mastpoints may have a PLAN-version. This function makes sure that the PLAN-version,
 * if it exists, is colored instead of the hidden LIVE Mastpoint.
 * 
 * @param entity Mastpoint start or Mastpoint end. 
 * @returns Entity[]
 */
const getMastpointWithinContext = (entity: Entity) => {
	const activeDataSource = get(mapModeStore).activeDataSource;

	// If any of these conditions are true, the mastpoint should be marked without further consideration.
	// 1. If current context is live
	// 2. If the entity is not hidden
	if(!activeDataSource || entity.show){
		return entity;
	} else {
		// If not, the entity in question is a hidden live-object which needs a reverse lookup among plan entities.
		let planEntity: Entity | undefined;

		activeDataSource.entities.values.forEach((entityInPlan) => {
			if(!entityInPlan.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT)) return;

			const proximityId = getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(entityInPlan).data.planEntityData?.liveObjectId;
			if(ENTITY_ID_PREFIX.MASTPOINT + proximityId === entity.id){
				planEntity = entityInPlan;
			}
		});

		if(planEntity) return planEntity;
		return entity;
	}
	
}

/**
 * Handle toggle of virtual supports(/forankringer). Used for toggling visibility of
 * virtual supports on/off (setting) and/or updating map to reflect current option.
 *
 * @param toggle - If true, toggle visibility setting on/off. If false, update entities according to current setting. Default true
 *
 */
const toggleVirtualSupports = (toggle = true) => {
	if (toggle) {
		mapToolsStore.update((store) => {
			store.displayVirtualSupports = !store.displayVirtualSupports;
			return store;
		});
	}

	const viewer = useCesiumViewer();
	const { mapdataDataSource } = useDataSources();
	const supportsVisible = get(mapToolsStore).displayVirtualSupports;
	const displayedDatasourceNames = get(mapModeStore).displayedDataSources;
	let planPoleId: number[] = [];
	let planDatasources = displayedDatasourceNames.map((datasource) => {
		if (datasource) {
			const datasourceObject = viewer.dataSources.getByName(datasource)[0];
			if (datasourceObject) {
				return datasourceObject;
			}
		}
	});

	planDatasources.forEach((planDatasource) => {
		const virtualSupports = planDatasource?.entities.values.filter((entity) => {
			const entityPropertyBag: ISupportModel = getEntityPropertyBag(entity);

			if (
				entityPropertyBag &&
				entityPropertyBag.entityType === 'SUPPORT' &&
				entityPropertyBag?.data?.calculated
			) {
				return true;
			}
		});

		virtualSupports?.forEach((entity) => {
			const entityPropertyBag = getEntityPropertyBag(entity);

			if (!entity.show) {
				const liveObjectReference = (entityPropertyBag as ISupportModel).data?.poleId;

				if (liveObjectReference) planPoleId.push(liveObjectReference);
			}
			entity.show = supportsVisible;
		});
	});

	const virtualsInLive = mapdataDataSource.entities.values
		.filter((entity) => {
			const entityPropertyBag = getEntityPropertyBag(entity);
			//Only handle visible mastpoints with poles
			if (entityPropertyBag && entityPropertyBag.entityType === 'MASTPOINT') {
				if (entity.show) {
					return true;
				} else {
					return false;
				}
			}
		})
		.map((visibleMastpoints) => {
			const entityPropertyBag = getEntityPropertyBag(
				visibleMastpoints
			) as IMastPointFullModel.MastPointPropertyBagModel;
			const polesInMastpoint = entityPropertyBag.data.poles;

			if (polesInMastpoint.length) {
				return polesInMastpoint.map((pole) => {
					return pole.id;
				});
			}
		})
		.flat();

	//Loops poleIds which has a mastpoint that is visible
	virtualsInLive?.forEach((poleId) => {
		const supportPoleEntity = mapdataDataSource.entities.getById('supportPoleId:' + poleId);
		if (supportPoleEntity) {
			supportPoleEntity.show = supportsVisible;
		}
	});
};

/**
 * Handle toggle of physical supports(/forankringer). Used for toggling visibility of
 * physical supports on/off (setting) and/or updating map to reflect current option.
 *
 * @param toggle - Wether to toggle visibility on/off. Default true
 *
 */
const togglePhysicalSupports = (toggle = true) => {
	if (toggle) {
		mapToolsStore.update((store) => {
			store.displayPhysicalSupports = !store.displayPhysicalSupports;
			return store;
		});
	}

	const viewer = useCesiumViewer();
	const { mapdataDataSource } = useDataSources();
	const supportsVisible = get(mapToolsStore).displayPhysicalSupports;
	const displayedDatasourceNames = get(mapModeStore).displayedDataSources;

	let liveObjectReferences: number[] = [];

	let planDatasources = displayedDatasourceNames.map((datasource) => {
		if (datasource) {
			const datasourceObject = viewer.dataSources.getByName(datasource)[0];
			if (datasourceObject) {
				return datasourceObject;
			}
		}
	});

	planDatasources.forEach((planDatasource) => {
		const physicalSupports = planDatasource?.entities.values.filter((entity) => {
			const entityPropertyBag = getEntityPropertyBag(entity) as ISupportModel;

			if (
				entityPropertyBag &&
				entityPropertyBag.entityType === 'SUPPORT' &&
				!entityPropertyBag.data.calculated
			) {
				return true;
			}
		});

		physicalSupports?.forEach((entity) => {
			const entityPropertyBag = getEntityPropertyBag(entity);
			if (!entity.show) {
				//Keep liveobjectreferences so we can hide
				const liveObjectReference = (entityPropertyBag as ISupportModel).data?.planEntityData
					?.liveObjectId;
				if (liveObjectReference) liveObjectReferences.push(liveObjectReference);
			}
			entity.show = supportsVisible;
		});
	});

	const physicalsInLive = mapdataDataSource.entities.values.filter((entity) => {
		const entityPropertyBag = getEntityPropertyBag(entity) as ISupportModel;

		if (
			entityPropertyBag &&
			entityPropertyBag.entityType === 'SUPPORT' &&
			!entityPropertyBag.data.calculated
		) {
			return true;
		}
	});

	physicalsInLive?.forEach((entity) => {
		if (
			liveObjectReferences.length &&
			liveObjectReferences.includes(parseInt(entity.id.split(':')[1]))
		) {
			entity.show = false;
		} else {
			entity.show = supportsVisible;
		}
	});
};

/**
 * Maplayer-tool which handles toggling labels for operational marking on mastpoints.
 * Also adds event listeners which responds to updates in datasources.
 */
const toggleOperationalMarkingLabels = async () => {
	const viewer = useCesiumViewer();
	const { operationalMarkingDatasource, mapdataDataSource } = useDataSources();

	const operationalMarkingLabelsEnabled = get(mapToolsStore).displayOperationalMarkingLabels;

	mapToolsStore.update((store) => {
		store.displayOperationalMarkingLabels = !operationalMarkingLabelsEnabled;
		return store;
	});

	if (!operationalMarkingLabelsEnabled) {
		if (!viewer.dataSources.contains(operationalMarkingDatasource))
			viewer.dataSources.add(operationalMarkingDatasource);

		const activeDataSources = [mapdataDataSource];
		const currentlyActiveDatasource = get(mapModeStore).activeDataSource;
		if (currentlyActiveDatasource) activeDataSources.push(currentlyActiveDatasource);

		activeDataSources.forEach((datasource) => {
			datasource.entities.values.forEach((entity, i) => {
				if (!entity.show) return;

				const entityPropertyBag = getEntityPropertyBag(
					entity
				) as IMastPointMinimalModel.MastPointPropertyBagModel;
				if (!entityPropertyBag || entityPropertyBag?.entityType !== 'MASTPOINT') return;
				
				const mastPointPosition = entity.position?.getValue(new JulianDate());
				const operationalMarking = entity.name?.split(':')[1].replace(/\s/g, '');
				let combinedZ = mastPointPosition!.z;

				if (entity.cylinder) {
					const mastPointHeight = entity.cylinder?.length;
					combinedZ += mastPointHeight?.getValue(new JulianDate()) / 1.5;
				}

				operationalMarkingDatasource.entities.add(
					new Entity({
						//Mastpoints does not guarantee a unique operationalmarking. Adding index to separate possible duplicates
						id: `Operationalmarking_label:${operationalMarking}:${i}`,
						label: new LabelGraphics({
							backgroundColor: new Color(0.047, 0, 0.255, 0.8),
							showBackground: true,
							verticalOrigin: VerticalOrigin.CENTER,
							horizontalOrigin: HorizontalOrigin.CENTER,
							backgroundPadding: new Cartesian2(10, 6),
							scaleByDistance: new NearFarScalar(0.0, 1.0, 8.0e6, 0.0),
							text: operationalMarking,
							font: '14px Poppins',
							disableDepthTestDistance: Number.POSITIVE_INFINITY
						}),
						position: new Cartesian3(
							mastPointPosition?.x,
							mastPointPosition?.y,
							combinedZ
						)
					})
				);
			});
		});
	} else {
		operationalMarkingDatasource.entities.removeAll();
	}
};

/**
 * Used to toggle the highlighting of a specific polygon, for instance when mouse-over (toggle on) or mouse-leave (toggle off)
 * @param polygonEntity - The polygon entity to toggle highlight for.
 * @param highlight - On (true) or off (false). `On` will cause the polygon to turn red with 0.5 opacity. `Off` will restore the default white 0.7 opacity.
 */
const toggleHightlightPolygon = ({
	polygonEntity,
	highlight
}: {
	polygonEntity: Entity;
	highlight: boolean;
}) => {
	if (polygonEntity.polygon) {
		if (highlight) {
			polygonEntity.polygon.material = new ColorMaterialProperty(Color.RED.withAlpha(0.5));
		} else {
			polygonEntity.polygon.material = new ColorMaterialProperty(Color.WHITE.withAlpha(0.7));
		}
	} else {
		console.log('polygonEntity has no `.polygon`:');
		console.log(polygonEntity);
	}
};

/**
 * Add a single temporary line to the map.
 * The function's primary use is to visualise lines that are being configured, before being submitted/added to the dataset
 * @param dataLine The line to be added
 */
async function addTemporaryLineToMap(dataLine: {
	lat1: number;
	lng1: number;
	lat2: number;
	lng2: number;
	x1Y1Height: number;
	x2Y2Height: number;
	temporaryLineId: string;
}): Promise<Entity> {
	const viewer = useCesiumViewer();
	const { temporaryConfiguredLinesDataSource } = useDataSources();
	const scene = viewer.scene;
	const positions = [];

	// Extract coordinates for each line and put them in the `positions` array
	const lineMidpoint = midpoint(
		point([dataLine.lng1, dataLine.lat1]),
		point([dataLine.lng2, dataLine.lat2])
	);

	const [midpointLng, midpointLat] = lineMidpoint.geometry.coordinates;

	positions.push(
		Cartographic.fromDegrees(dataLine.lng1, dataLine.lat1),
		Cartographic.fromDegrees(midpointLng, midpointLat),
		Cartographic.fromDegrees(dataLine.lng2, dataLine.lat2)
	);

	// Retrieve height values (async.) based on the terrain provider for the array of positions generated above.
	const updatedPositions = await sampleTerrainMostDetailed(scene.terrainProvider, positions);

	const lineMidpointHeight = (dataLine.x1Y1Height + dataLine.x2Y2Height) / 2;

	const OFFSET = 0.2;

	const linePoint1 = [
		dataLine.lng1,
		dataLine.lat1,
		updatedPositions[0].height + dataLine.x1Y1Height - OFFSET
	];

	const linePointMiddle = [
		midpointLng,
		midpointLat,
		updatedPositions[1].height - OFFSET + (lineMidpointHeight - 0.75)
	];

	const linePoint2 = [
		dataLine.lng2,
		dataLine.lat2,
		updatedPositions[2].height + dataLine.x2Y2Height - OFFSET
	];

	const addedEntity = temporaryConfiguredLinesDataSource.entities.add({
		id: 'temporary-line:' + dataLine.temporaryLineId,
		name: `Konfigurert midlertidig linje: ${dataLine.temporaryLineId}`,
		properties: new PropertyBag({
			data: null,
			entityType: EntityTypes.TEMPORARY_LINE
		}),
		polyline: {
			positions: Cartesian3.fromDegreesArrayHeights([
				...linePoint1,
				...linePointMiddle,
				...linePoint2
			]),
			width: 10,
			arcType: ArcType.RHUMB,
			// If the line is merged (`isMerged`), color it blue - otherwise brown
			material: new PolylineDashMaterialProperty({
				color: Color.DARKCYAN,
				dashPattern: parseInt('1010101010101010', 2)
			})
		}
	});

	return addedEntity;
}

const mastPointMaterialColorDecider = (
	mastPoint: IMastPointFullModel.MastPoint
): ColorMaterialProperty => {
	if (mastPoint.hasDamages) {
		return setColorProperty(Color.ORANGE);
	} else if (mastPoint.hasVirtuals) {
		return setColorProperty(Color.LIGHTSKYBLUE);
	} else if (get(tabletModeStore)) {
		if (mastPoint.hasDamages || mastPoint.hasImages) {
			return setColorProperty(Color.SPRINGGREEN);
		}

		return setColorProperty(Color.FUCHSIA);
	} else {
		return setColorProperty(Color.GAINSBORO);
	}
};

const getCurrentCameraMagnitude = (viewer: Viewer) => {
	const cameraPosition = viewer.camera.positionWC;
	const ellipsoidPosition = viewer.scene.globe.ellipsoid.scaleToGeodeticSurface(cameraPosition);
	return Cartesian3.magnitude(
		Cartesian3.subtract(cameraPosition, ellipsoidPosition, new Cartesian3())
	);
};

/**
 * Retrieves center of map based on camera position and window size
 *
 * @param viewer Cesium Viewer
 * @returns Viewport center in [lng, lat]
 */
const getCurrentViewportCenter = (viewer: Viewer): [number, number] | undefined => {
	const windowPosition = new Cartesian2(
		viewer.container.clientWidth / 2,
		viewer.container.clientHeight / 2
	);
	const pickRay = viewer.scene.camera.getPickRay(windowPosition) as Ray;
	if (!pickRay) return;
	const pickPosition = viewer.scene.globe.pick(pickRay, viewer.scene);
	if (!pickPosition) return;
	const pickPositionCartographic =
		viewer.scene.globe.ellipsoid.cartesianToCartographic(pickPosition);
	const pickRayLat = pickPositionCartographic.latitude * (180 / Math.PI);
	const pickRayLng = pickPositionCartographic.longitude * (180 / Math.PI);

	return [pickRayLng, pickRayLat];
};

/**
 * En funksjon som spesifikt retter seg mot corridor-entities slik at bildet beregner høyde ut fra entitetens bounding box.
 * TEST Funksjonen. Hvis utsnittet er i overkant tett, øk padding (f.eks. 0.15) eller safetyScale (f.eks. 1.1).
 * 
 * @param viewer 
 * @param corridorEntity 
 * @param opts 
 * @returns 
 */
const flyToCorridorNadir = (
  viewer: Viewer,
  corridorEntity: Entity,
  opts: {
    duration?: number;
    padding?: number;              // ekstra “visuell” margin (prosent). default 10%
    minAltitudeMeters?: number;    // gulv for høyde
    safetyScale?: number;          // ekstra sikkerhetsfaktor >1.0
  } = {}
): Promise<void> => {
  const duration = opts.duration ?? 1.2;
  const padding = opts.padding ?? 0.10;
  const minAlt = opts.minAltitudeMeters ?? 30;
  const safetyScale = opts.safetyScale ?? 1.05;

  const { scene } = viewer;
  const { camera } = scene;

  const corridor = corridorEntity.corridor;
  if (!corridor) throw new Error("Forventer en Corridor-entity.");

  const t = viewer.clock.currentTime;

  // Posisjoner + bredde
  const positions: Cartesian3[] = corridor.positions?.getValue() ?? [];
  if (!positions.length) throw new Error("Corridor har ingen posisjoner.");

  const widthMeters = corridor.width?.getValue() ?? 0;

  // Kartografisk + minimal rektangel (håndterer datolinjen)
  const ellipsoid = Ellipsoid.WGS84;
  const cartos = positions
    .map(p => ellipsoid.cartesianToCartographic(p))
    .filter(Boolean) as Cartographic[];

  let rect = Rectangle.fromCartographicArray(cartos);

  // Buffer med halv bredde (meter -> radianer), + prosentvis padding
  const R = ellipsoid.maximumRadius; // ~6378137 m
  const half = widthMeters / 2;

  const center = Rectangle.center(rect);
  const midLat = center.latitude;

  const dLat = half / R;
  const dLon = half / (R * Math.max(1e-6, Math.cos(midLat)));

  const latSpan = rect.north - rect.south;
  const lonSpan = Rectangle.computeWidth(rect);

  const padLat = latSpan * padding;
  const padLon = lonSpan * padding;

  rect = new Rectangle(
    rect.west  - dLon - padLon,
    rect.south - dLat - padLat,
    rect.east  + dLon + padLon,
    rect.north + dLat + padLat
  );

  // Regn ut nødvendig høyde for at hele rect får plass i viewport ved nadir
  function getFovyAndAspect(): { fovy: number; hfov: number } {
    // Forsøk å hente fovy direkte
    let fovy: number | undefined;
    const fr = camera.frustum as any;

    if (typeof fr.fovy === "number") {
      fovy = fr.fovy;
    } else if (typeof fr.top === "number" && typeof fr.bottom === "number" && typeof fr.near === "number") {
      // Off-center frustum
      const vTop = Math.atan(Math.abs(fr.top) / fr.near);
      const vBottom = Math.atan(Math.abs(fr.bottom) / fr.near);
      fovy = vTop + vBottom;
    } 

	if(typeof fr.fovy !== "number" || !fovy){
		fovy = Math.PI / 3; // fallback 60°
	}

    const w = scene.drawingBufferWidth || 1;
    const h = scene.drawingBufferHeight || 1;
    const aspect = w / h;
    const hfov = 2 * Math.atan(Math.tan(fovy / 2) * aspect);
    return { fovy, hfov };
  }

  const { fovy, hfov } = getFovyAndAspect();

  // Rektangelets utbredelse i meter 
  const latSpanM = R * (rect.north - rect.south);
  const lonSpanM = R * Math.cos(midLat) * Rectangle.computeWidth(rect);

  const halfH = latSpanM / 2;
  const halfW = lonSpanM / 2;

  // Høyde som kreves for at både vertikal og horisontal utbredelse får plass i FOV
  let altitude =
    Math.max(
      halfH / Math.tan(fovy / 2),
      halfW / Math.tan(hfov / 2)
    ) * safetyScale;

  altitude = Math.max(altitude, minAlt);

  // Kamera-destinasjon = sentrum av rect, over terrenget + altitude
  const centerCarto = Rectangle.center(rect);
  const terrainHeight =
    (scene.globe && scene.globe.getHeight)
      ? (scene.globe.getHeight(centerCarto) ?? 0)
      : 0;

  const destCarto = new Cartographic(
    centerCarto.longitude,
    centerCarto.latitude,
    terrainHeight + altitude
  );
  const destination = Ellipsoid.WGS84.cartographicToCartesian(destCarto);

  return new Promise<void>((resolve) => {
    camera.flyTo({
      destination,
      orientation: {
        heading: 0.0,            // Nord opp
        pitch: -Math.PI / 2,     // Rett ned
        roll: 0.0
      },
      duration,
      pitchAdjustHeight: 0,
      complete: resolve
    });
  });
}

const getCurrentCesiumCameraPosition = (camera: Camera) => {
	return {
		position: camera.position.clone(),
		direction: camera.direction.clone(),
		up: camera.up.clone(),
		right: camera.right.clone(),
		frustum: camera.frustum
	};
};

const setCesiumCameraPosition = (cameraPosition: {
	position: Cartesian3;
	direction: Cartesian3;
	up: Cartesian3;
	right: Cartesian3;
	frustum: PerspectiveOffCenterFrustum;
}) => {
	const viewer = useCesiumViewer();
	viewer.scene.camera.position = cameraPosition.position;
	viewer.scene.camera.direction = cameraPosition.direction;
	viewer.scene.camera.up = cameraPosition.up;
	viewer.scene.camera.right = cameraPosition.right;
	viewer.scene.camera.frustum = cameraPosition.frustum;
};

const getAndDisplayCurrentUserPosition = async ({ flyToUser }: { flyToUser: boolean }) => {
	if (navigator.geolocation) {
		const viewer = useCesiumViewer();
		navigator.geolocation.getCurrentPosition(async function (position) {
			const { longitude, latitude } = position.coords;
			const userCurrentPositionPointEntity = viewer.entities.getById('currentPosition');

			const updatedPositions = await sampleTerrainMostDetailed(viewer.scene.terrainProvider, [
				Cartographic.fromDegrees(longitude, latitude)
			]);

			if (userCurrentPositionPointEntity) {
				userCurrentPositionPointEntity.position = new ConstantPositionProperty(
					Cartesian3.fromDegrees(longitude, latitude, updatedPositions[0].height)
				);
			}

			if (flyToUser) {
				flyToLatLngLocation({
					latitude,
					longitude,
					height: updatedPositions[0].height + 100
				});
			}
		});
	} else {
		alert('Din enhet eller nettleser har ikke støtte for geolokasjon.');
	}
};

const createPoint = ({
	position,
	referencePolygonId,
	sandbox_polygonDataSource,
	entityType
}: {
	position: Cartesian3;
	referencePolygonId: string;
	sandbox_polygonDataSource: CustomDataSource;
	entityType: keyof typeof EntityTypes;
}) => {
	return sandbox_polygonDataSource.entities.add({
		position: position,
		// @ts-ignore Custom SmartGIS entity types
		type: entityType,
		properties: new PropertyBag({
			entityType: entityType,
			parentPolygonId: referencePolygonId
		}),
		point: new PointGraphics({
			color: Color.BLUE.withAlpha(0.5),
			outlineColor: Color.BLUE.withAlpha(0.5),
			outlineWidth: 10,
			pixelSize: 5,
			disableDepthTestDistance: Infinity,
			heightReference: HeightReference.CLAMP_TO_GROUND
		})
	});
};

/**
 * Used whenever a plan-version of a mastpoint is generated by a change in mastpoint data. This change should reselect the newly created plan object
 * and optimally restore state of floating sidemenu. 
 * @param mastpointId - The ID of the mastpoint which is updated (Live ID)
 * @param czmlObjects - The czml response (json) of the updated czml 
 */
const processCzmlAndReselectMastPoint = async (mastpointId: number, czmlObjects: CzmlResponse) => {
	await processPlanCzml(czmlObjects);
	setSelectedEntity(undefined);

	const activeDataSource = get(mapModeStore).activeDataSource;
	if (activeDataSource) {
		const newlyCreatedMastpoint = czmlObjects.find((czmlItem) => {
			if ('properties' in czmlItem) {
				return (
					czmlItem.properties?.data?.planEntityData?.liveObjectId ===
					mastpointId
				);
			} else {
				return false;
			}
		});

		const newlyCreatedMastpointEntity = newlyCreatedMastpoint
			? activeDataSource.entities.getById(newlyCreatedMastpoint?.id)
			: null;

		if (newlyCreatedMastpointEntity) {
			setSelectedEntity(newlyCreatedMastpointEntity);
		}
		
	}
}

/**
 * TODO: Revisit as soon as we have relevant data from backend. Search is still dependant on this function, but should pass lat/lng so that we avoid having to fetch 2x.
 * This function handles several tasks:
 * - Fetches Data object for the mastpoint
 * - If a mastpoint is selected, but outside current rendered area, it fetches entities in the area around the mastpoint
 * - Processes the fetched CZML into a datasource
 * - If selecting a mastpoint outside rendered area, it flies to the mastpoint and selects it (By search).
 * @param mastPointId numeric ID of mastPoint to refetch
 */
const refetchMastPointCZML = async ({
	mastPointId,
	flyToMastPoint = false,
    coordinates = null,
    isLive = true
}: {
	mastPointId: number;
	flyToMastPoint?: boolean;
    coordinates?: [number, number] | null;
    isLive?: boolean;
}) => {
	const viewer = useCesiumViewer();
	if (mastPointId) {
        const {mapdataDataSource} = useDataSources();
		try {
			//const mastPointResource = createMastPointDataResource(mastPointId);
			//const mastPointJson = await mastPointResource.fetchJson();
			//const documentDataSource = findDataSourceByCzml(mastPointJson);
            //const documentDataSource = findDataSourceByCzml(mastpointData.czml);
			//const {partnerCompanies, enablePartnerColors, showPartnerEntities} = get(mapSettingsState);

			/*if (!documentDataSource) {
				toast({
					type: 'warning',
					title: 'Datafeil i mastepunkt',
					description: 'Mastepunktet hører ikke til en datakilde.',
				});
				return;
			}*/
            const planDatasource = get(mapModeStore).activeDataSource;

            let selectedMastPoint = isLive ? mapdataDataSource.entities.getById(ENTITY_ID_PREFIX.MASTPOINT + mastPointId) : planDatasource?.entities.getById(ENTITY_ID_PREFIX.MASTPOINT + mastPointId);

			const mastPointIdString = ENTITY_ID_PREFIX.MASTPOINT + mastPointId;
			const entityInArea = selectedMastPoint;

            let documentDataSource = isLive ? mapdataDataSource : planDatasource;

			// Only if selecting an entity out of rendered area. Load area around entity
			if (!entityInArea && coordinates?.length) {
				await fetchEntitiesInArea({
					sceneCenter: { longitude: coordinates[1], latitude: coordinates[0] },
					reloadArea: false
				});
			}

            if(!documentDataSource){
                console.error('Could not find documentDataSource for mastpoint CZML processing.');
                return;
            }
			const fetchedEntity = documentDataSource.entities.getById(mastPointIdString);

			if (!fetchedEntity) {
				toast({
					type: 'warning',
					title: 'Teknisk feil',
					description: 'Finner ikke valgt mastepunkt i datasettet. Hvis feilen vedvarer, kontakt SmartGIS.',
				});
				return;
			}
            
			//mastPointColorPicker(fetchedEntity, documentDataSource, enablePartnerColors, partnerCompanies, showPartnerEntities);

			// Only if selecting an entity out of rendered area. Fly to entity and select.
			if (fetchedEntity && flyToMastPoint && coordinates?.length) {
               
				flyAboveEntity({
                    viewer,
                    entity: fetchedEntity
                })

					setSelectedEntity(fetchedEntity)
			}

			/*if (fetchedEntity) {
				mastPoint.set(
					getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(fetchedEntity).data
				);
			}
			if (get(mastPoint)?.poles?.length > 0) {
				selectedPole.set(
					get(mastPoint).poles.find(
						(pole) => !pole.planEntityData || !pole.planEntityData.isMarkedForDeletion
					)
				);
			} else {
				toast({
					type: 'error',
					title: 'Feil i datagrunnlaget',
					description: 'Dette mastepunktet har ingen stolper.'
				});
			}*/
			
		} catch (error) {
			//@ts-ignore
			//mastPoint.set(null);
			//selectedPole.set(undefined);
			if (error instanceof RequestErrorEvent) {
				if (error.statusCode === 401) {
					handleUnauthorizedResourceFetch(error);
				} else {
					toast({
						type: 'error',
						title: 'Feil ved innhenting av mastepunkt',
						description: `Det oppstod et problem ved uthenting av mast <b>${mastPointId}</b>. Vent litt og marker masten i kartet på nytt for å laste oppdatert mastepunkt-data.`
					});
				}
			} else if (error instanceof HTTPError) {
				toast({
						type: 'error',
						title: 'Feil ved innhenting av mastepunkt',
						description: `Det oppstod et problem ved uthenting av mast <b>${mastPointId}</b>. Vent litt og marker masten i kartet på nytt for å laste oppdatert mastepunkt-data.`
					});
			} else {
				console.error(error);
				toast({
					type: 'error',
					title: 'Feil ved innhenting av mastepunkt',
					description: `En uventet feil oppstod ved uthenting av mast <b>${mastPointId}</b>.`
				});
			}
		}
	}
};

type FlyOptions = {
    heading: number;
    pitch: number;
    roll: number;
    range: number;
    duration: number;
};

const flyAboveEntity = (
    {
        viewer, 
        entity, 
        options = {}
    }: {
        viewer: Viewer;
        entity: Entity;
        options?: Partial<FlyOptions>;
    }
) => {
    // sets default options if some or all are missing
    const {
        heading = viewer.camera.heading,
        pitch = CesiumMath.toRadians(-35),
        roll = 0,
        range = 30,
        duration = 1.5
    } = options;

    const target = entity.position?.getValue();
    if (!target) return;

    // Build ENU frame at the target
    const enu = Transforms.eastNorthUpToFixedFrame(target);

    // Local ENU offset from the target (east, north, up)
    // Heading is measured from north, positive east (Cesium convention)
    const cosPitch = Math.cos(pitch);
    const sinPitch = Math.sin(pitch);

    const east = range * cosPitch * Math.sin(heading);
    const north = range * cosPitch * Math.cos(heading);
    const up = range * (-sinPitch); // pitch is negative when looking down

    const localOffset = new Cartesian3(east, north, up);

    // Convert local ENU offset into ECEF world coords
    const destination = Matrix4.multiplyByPoint(enu, localOffset, new Cartesian3());

    // Compute orientation that looks at the target from destination
    const toTarget = Cartesian3.normalize(
        Cartesian3.subtract(target, destination, new Cartesian3()),
        new Cartesian3()
    );

    // Express that direction in the destination's ENU frame to derive heading/pitch
    const enuAtDest = Transforms.eastNorthUpToFixedFrame(destination);
    const invEnuAtDest = Matrix4.inverse(enuAtDest, new Matrix4());
    const localDir = Matrix4.multiplyByPointAsVector(invEnuAtDest, toTarget, new Cartesian3());
    Cartesian3.normalize(localDir, localDir);

    const lookHeading = Math.atan2(localDir.x, localDir.y); // x=east, y=north
    const lookPitch   = Math.asin(localDir.z);              // z=up

    viewer.camera.flyTo({
        destination,
        orientation: {
        heading: lookHeading,
        pitch: lookPitch,
        roll
        },
        duration
    });


}

const distance3D = (x1: number, y1: number, z1: number, x2: number, y2: number, z2: number) => {
	const dx = x2 - x1;
	const dy = y2 - y1;
	const dz = z2 - z1;
	return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

/**
 * Gets the distance between two Cartographic points (radians) in meters
 * @param pointA Cartographic A
 * @param pointB Cartographic B
 * @returns distance in meters
 */
const distanceBetweenTwoCartographicPoints = (pointA: Cartographic, pointB: Cartographic) => {
	const pointACartesian = Cartesian3.fromRadians(pointA.longitude, pointA.latitude);
	const pointBCartesian = Cartesian3.fromRadians(pointB.longitude, pointB.latitude);

	return Cartesian3.distance(pointACartesian, pointBCartesian);
};

/**
 * Produces an array in Cartesian3 coordinates which can be used to create a rectangle.
 * This function takes a center point in lng/lat and a radius in meters
 *
 * @param lng longitude in degrees
 * @param lat latitude in degrees
 * @param radius radius in meters
 * @returns Cartesian3[]
 */
const createCartesianAreaByLngLatCenter = (lng: number, lat: number, radius: number) => {
	//Earth’s radius, sphere
	const R = 6378137;

	//offsets in meters
	const dn = radius;
	const de = radius;

	//Coordinate offsets in radians
	const dLat = dn / R;
	const dLng = de / (R * Math.cos((Math.PI * lat) / 180));

	//OffsetPosition, decimal degrees
	const latOAdd = lat + (dLat * 180) / Math.PI;
	const lngOAdd = lng + (dLng * 180) / Math.PI;

	const latOSubtract = lat - (dLat * 180) / Math.PI;
	const lngOSubtract = lng - (dLng * 180) / Math.PI;

	return [
		Cartesian3.fromDegrees(lngOAdd, latOAdd),
		Cartesian3.fromDegrees(lngOSubtract, latOSubtract)
	];
};

const getPolylineDistanceInMeters = async (
	positions: Cartesian3[],
	viewer: Viewer,
	useTerrain = false
) => {
	/* const surfacePositions = PolylinePipeline.generateArc({
		positions: positions
	}); */
	const positionsCopy = [...positions];

	const ellipsoid = viewer.scene.globe.ellipsoid;
	const terrainProvider = viewer.terrainProvider;

	let totalDistanceInMeters = 0;

	for (let i = 1; i < positionsCopy.length; i++) {
		let distance;

		if (useTerrain) {
			const startPointOrig = positionsCopy[i - 1];
			const endPointOrig = positionsCopy[i];

			const startPoint = ellipsoid.cartesianToCartographic(startPointOrig);
			const endPoint = ellipsoid.cartesianToCartographic(endPointOrig);

			const terrainHeights = await sampleTerrainMostDetailed(terrainProvider, [
				startPoint,
				endPoint
			]);

			startPoint.height = terrainHeights[0].height;
			endPoint.height = terrainHeights[1].height;

			const geodesic = new EllipsoidGeodesic(startPoint, endPoint);
			distance = geodesic.surfaceDistance;
		} else {
			const start = positionsCopy[i - 1];
			const end = positionsCopy[i];

			distance = distance3D(start.x, start.y, start.z, end.x, end.y, end.z);
		}

		totalDistanceInMeters += distance;
	}

	return { totalDistanceInMeters };

	/* const totalDistanceInKm = totalDistanceInMeters * 0.001;
	const totalDistanceIn3DKm = totalDistanceIn3D * 0.001;
	return { totalDistanceInMeters, totalDistanceInKm, totalDistanceIn3D, totalDistanceIn3DKm }; */
};

/*
 * Used in conjunction with CZML drawing export. This creates a single CZML packet (entity object).
 */
function createCzmlPacket(entity: Entity, time: JulianDate): any {
	const packet: any = {
		id: entity.id
	};

	if (entity.polyline instanceof PolylineGraphics) {
		const positions = entity.polyline.positions?.getValue(time);
		// const dontClamp = !entity.polyline?.clampToGround;
		const cclamp = entity.polyline?.clampToGround?.getValue(time);
		// console.log('z' + JSON.stringify(entity.polyline.zIndex));
		

		if (positions) {
			const entityMaterial = entity.polyline.material.getValue(time);
			const entityMaterialColor = {
				color: {
					rgba: entity.polyline.material.getValue(time).color.toBytes()
				}
			};
			const clampToGroundVal = entity.polyline.clampToGround?.getValue(time);

			// The outer object wrapper (property name) differs between dashed and solid polylines
			// eslint-disable-next-line no-prototype-builtins
			const czmlMaterial = entityMaterial.hasOwnProperty('dashLength')
				? {
						polylineDash: {
							...entityMaterialColor
						}
				  }
				: {
						solidColor: {
							...entityMaterialColor
						}
				  };

			packet.polyline = {
				positions: {
					cartographicDegrees: positions
						.map((position: Cartesian3) => {
							const cartographic = Cartographic.fromCartesian(position);
							return [
								(cartographic.longitude * 180) / Math.PI,
								(cartographic.latitude * 180) / Math.PI,
								cartographic.height
							];
						})
						.flat()
				},
				clampToGround: clampToGroundVal ? clampToGroundVal : false,
				material: czmlMaterial,
				width: entity.polyline.width?.getValue(time)
			};
		}
	} else if (entity.rectangle instanceof RectangleGraphics) {
		const coordinates = entity.rectangle.coordinates?.getValue(time);

		if (coordinates) {
			const west = (coordinates.west * 180) / Math.PI;
			const south = (coordinates.south * 180) / Math.PI;
			const east = (coordinates.east * 180) / Math.PI;
			const north = (coordinates.north * 180) / Math.PI;

			// There are some scenarios in which drawn rectangles are not visible due to their coordinates, and should not be included in the export.
			if (west === 0 && south === 0 && east === 0 && north === 0) {
				return null;
			}

			packet.rectangle = {
				coordinates: {
					wsenDegrees: [west, south, east, north]
				},
				material: {
					solidColor: {
						color: {
							rgba: entity.rectangle.material.getValue(time).color.toBytes()
						}
					}
				}
			};
		}
	} else if (entity.box instanceof BoxGraphics) {
		const cartesianPosition = entity.position?.getValue(time);
		const quaternionValue = entity.orientation?.getValue(time);

		if (cartesianPosition) {
			let convertedPosition;
			const convertedOrientation: number[] = [];

			const cartographic = Cartographic.fromCartesian(cartesianPosition);
			convertedPosition = [
				(cartographic.longitude * 180) / Math.PI,
				(cartographic.latitude * 180) / Math.PI,
				cartographic.height
			];

			if (quaternionValue) {
				Quaternion.pack(quaternionValue, convertedOrientation);
				packet.orientation = {
					unitQuaternion: convertedOrientation
				};
			}

			packet.position = {
				cartographicDegrees: convertedPosition
			};
			packet.box = {
				dimensions: {
					cartesian: Object.values(entity.box.dimensions?.getValue(time))
				},
				material: {
					solidColor: {
						color: {
							rgba: entity.box.material?.getValue(time).color.toBytes()
						}
					}
				}
			};
		}
	} else if (entity.polygon instanceof PolygonGraphics) {
		const hierarchy = entity.polygon.hierarchy?.getValue(time);
		if (hierarchy) {
			packet.polygon = {
				positions: {
					cartographicDegrees: hierarchy.positions
						.map((position: Cartesian3) => {
							const cartographic = Cartographic.fromCartesian(position);
							return [
								(cartographic.longitude * 180) / Math.PI,
								(cartographic.latitude * 180) / Math.PI,
								cartographic.height
							];
						})
						.flat()
				},
				material: {
					solidColor: {
						color: {
							rgba: entity.polygon.material.getValue(time).color.toBytes()
						}
					}
				}
			};
		}
	} else if (entity.label instanceof LabelGraphics) {
		const text = entity.label.text?.getValue(time);
		const position = entity.position?.getValue(time);
		if (text && position) {
			const cartographic = Cartographic.fromCartesian(position);
			const longitude = (cartographic.longitude * 180) / Math.PI;
			const latitude = (cartographic.latitude * 180) / Math.PI;
			//const height = cartographic.height;

			packet.position = {
				cartographicDegrees: [longitude, latitude, 0]
			};

			packet.label = {
				text: text,
				verticalOrigin: 'BOTTOM',
				heightReference: 'CLAMP_TO_GROUND',
				horizontalOrigin: 'LEFT',
				font: entity.label.font?.getValue(time),
				fillColor: {
					rgba: entity.label.fillColor?.getValue(time).toBytes()
				},
				showBackground: true,
				disableDepthTestDistance: 1.2742018 * 10 ** 7,
				eyeOffset: {
					cartesian: [
						entity.label.eyeOffset?.getValue(time).x,
						entity.label.eyeOffset?.getValue(time).y,
						entity.label.eyeOffset?.getValue(time).z
					]
				},
				pixelOffset: {
					cartesian2: [8, 0]
				},
				pixelOffsetScaleByDistance: new NearFarScalar(1.5e2, 3.0, 1.5e7, 0.5),
				clampToGround: true
			};
		}
	} else if (entity.billboard instanceof BillboardGraphics) {
		const position = entity.position?.getValue(time);

		if (position) {
			packet.position = {
				cartesian: [position.x, position.y, position.z]
			};

			packet.billboard = {
				image: entity.billboard.image?.getValue(time),
				verticalOrigin: 'BOTTOM',
				heightReference: 'CLAMP_TO_GROUND',
				clampToGround: true
			};
		}
	} else if (entity.corridor instanceof CorridorGraphics){
		const positions = entity.corridor.positions?.getValue();
		if(positions){
			packet.corridor = {
				positions: {
					cartographicDegrees: positions
						.map((position: Cartesian3) => {
							const cartographic = Cartographic.fromCartesian(position);
							return [
								(cartographic.longitude * 180) / Math.PI,
								(cartographic.latitude * 180) / Math.PI,
								cartographic.height
							];
						})
						.flat()
				},
				width: entity.corridor.width?.getValue(time),
				cornerType: "MITERED",
				heightReference : entity.corridor.heightReference?.getValue(),
				material: {
					solidColor: {
						color: {
							rgba: entity.id.startsWith('cablePath:') ? Color.BEIGE.toBytes() : Color.TRANSPARENT.toBytes()
						}
					}
				},
				zIndex: entity.id.startsWith('cablePath:') ? 10 : null
			};

		}else {
			console.log('No corridor positions found');
		}
	}

	packet.properties = entity.properties?.getValue(time);

	return packet;
}

function generateCzmlDocument(entityCollections: EntityCollection[]): any[] {
	const czml = [
		{
			id: 'document',
			name: 'SmartGIS CZML drawings export',
			version: '1.0'
		}
	];

	// Cesium requires a certain time to be set for when retrieving values from entities (.getValues()), this is because CZML entities can be time-dynamic (ours aren't).
	const time = new JulianDate();

	for (const entityCollection of entityCollections) {
		for (const entity of entityCollection.values) {
			const packet = createCzmlPacket(entity, time);
			if (packet && Object.keys(packet).length > 1) {
				czml.push(packet);
			}
		}
	}

	return czml;
}


export {
	cartesianToLatitudeLongitude,
	getPolygonBoundariesAsLatLngs,
	getMapEntitiesWithinBoundaries,
	getEntityPropertyBag,
	getCurrentCesiumCameraPosition,
	getCurrentCameraMagnitude,
	getCurrentViewportCenter,
	setCesiumCameraPosition,
	distanceBetweenTwoCartographicPoints,
	getAndDisplayCurrentUserPosition,
	getPolygonEdgesSortedByDistanceToPoint,
	getPolygonSelectableEdgePointsByPosition,
	getPolygonByPolygonReferenceId,
	doesPolygonContainDuplicateEdges,
	getPolylineDistanceInMeters,
	getEntityById,
	getCZMLEntityById,
	setSelectedEntity,
	upsertEntityProperty,
	createTerrainProfilePolylineBetweenEntities,
	flyToPolygonBoundingBox,
	flyToLatLngLocation,
	flyToEntityLocation,
	addTemporaryLineToMap,
	createPoint,
	createProtractor,
    reloadProtractor,
	createCartesianAreaByLngLatCenter,
	removeGroundPrimitive,
	removeAllProtractors,
	toggleEntityVisuals,
	toggleEntityVisualsForLineEditing,
	toggleEntityVisualsWithinLineSpan,
	toggleVirtualSupports,
	togglePhysicalSupports,
	toggleOperationalMarkingLabels,
	toggleHightlightPolygon,
	fetchMapData,
	fetchEntitiesInArea,
	runManualMapRefresh,
	processCzmlAndReselectMastPoint,
	refetchMastPointCZML,
	redrawOperationalMarkingsIfEnabled,
	createPolygonEntity,
    flyToEntityLocationView,
	//exportDataSourcesToKml,
    entityIsPartOfMastpoint,
	createTerrainOffsets,
	generateCzmlDocument,
	handleContextualizedCzmlResponse,
	setColorProperty,
	setPartnerColorProperty,
	flyToCorridorNadir,
	EntityTypes
};
